/**
 * **목록 화면의 서버 페이징·정렬 어휘 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 지금까지 목록 화면의 정렬은 전부 **클라이언트 정렬**이었다(`sortable` = 지금 그려진 행들만 다시 늘어놓기).
 * 목록을 서버 페이징으로 바꾸면 그 정렬은 「이 페이지 안에서만」이 되어 사람이 본 순서와 서버가 고른
 * 50건이 서로 다른 말을 한다. 그래서 페이징과 정렬은 **같은 축**이고, 그 변환을 한 곳에 둔다.
 *
 * 서버와의 계약(회차 0, 계획 §5.1):
 *
 * - 요청 쿼리 — `pageSize`(기본 [DEFAULT_PAGE_SIZE]) · `currPage`(0-base) · `sortProp`(컬럼 prop) ·
 *   `sortOrder`([SORT_ORDER]). 정렬 축이 비면 **두 키를 빼고** 보낸다([pagingQuery]) — 서버 기본 정렬이다.
 * - 응답 봉투 — `{content, totalElements, totalPages, number, size}`(Spring `Page`). 화면이 읽는 셋은
 *   [applyPageResult] 가 꺼낸다.
 * - 서버는 **화이트리스트 밖의 `sortProp` 을 무시하고** 기본 정렬로 답한다(화면 오류 없음).
 *
 * 상태를 화면에 반영하는 쪽은 `mixins/pagedListMixin`, 페이지 UI 는 `components/common/ListPagination.vue` 다.
 */

/** 페이지 크기 기본값 — 선택지 `[50, 100, 200, 300]` 의 첫 칸(계획 결정 9) */
export const DEFAULT_PAGE_SIZE = 50

/**
 * **전량** — 페이지 UI 가 없는 소비자가 「이 조건의 전부」를 요구하는 값이다.
 *
 * 같은 목록 API 를 화면 말고도 셀렉트·검색창·POS 임베드가 부르는데, 그쪽은 다음 장으로 넘어갈
 * 수단이 없으니 한 장이 곧 전부여야 한다. 종전에는 그 자리마다 「충분히 큰 수」(10000·300)를
 * 적었다 — 그것은 **상한을 추측한 값**이라 매장이 커지면 조용히 잘리고, 서버가 상한을 두면
 * 어느 쪽 수가 이겼는지도 알 수 없다. 0 은 추측이 아니라 **선언**이고 서버가 전량 모드로 받는다.
 * 봉투 모양은 그대로이므로 읽는 쪽은 [pageRows] 로 같다.
 */
export const ALL_ROWS_PAGE_SIZE = 0

/** Element UI 의 `sort-change` 가 쓰는 어휘 그대로 — 서버도 같은 낱말을 받는다 */
export const SORT_ORDER = Object.freeze({
  ASC: 'ascending',
  DESC: 'descending',
})

/**
 * 검색 조건에 페이징·정렬 축을 붙인다 — 뷰의 `initSearchForm()` 이 부른다.
 *
 * 네 키를 **처음부터 만들어 두는** 이유: Vue 2 는 나중에 붙인 속성을 반응형으로 만들지 않으므로,
 * 정렬을 처음 누른 순간 페이지 UI 가 그 변화를 못 본다.
 *
 * @param {object} searchForm 화면이 가진 검색 조건
 * @param {{pageSize?: number}} [options] 기본 페이지 크기(목록마다 다를 수 있다)
 * @returns {object} 페이징·정렬 축이 붙은 새 객체(인자는 건드리지 않는다)
 */
export function withPaging(searchForm, options) {
  const pageSize = (options && options.pageSize) || DEFAULT_PAGE_SIZE

  return Object.assign({}, searchForm, {
    pageSize: pageSize,
    currPage: 0,
    sortProp: null,
    sortOrder: null,
  })
}

/**
 * 페이지 봉투에서 화면이 쓰는 셋을 꺼낸다 — `{currPage, tableData, totalElements}`.
 *
 * **이상한 응답에도 값을 돌려준다**: 목록이 비거나(`content` 없음) 봉투가 아닌 응답이 와도 화면이 깨지지
 * 않아야 한다. 배열이 그대로 오는 갈래는 **페이징 전 서버**다 — appserver 를 먼저 배포하는 회차라 정상
 * 경로는 아니지만, 그 창에서 목록이 통째로 사라지는 것보다 전량을 그리는 편이 낫다.
 *
 * @param {*} result 서버 응답
 * @returns {{currPage: number, tableData: Array, totalElements: number}}
 */
export function applyPageResult(result) {
  if (Array.isArray(result)) {
    return { currPage: 0, tableData: result.slice(), totalElements: result.length }
  }

  if (!result || typeof result !== 'object') {
    return { currPage: 0, tableData: [], totalElements: 0 }
  }

  // 사본을 준다 — 배열 갈래와 같은 규칙이다. 화면은 받은 배열을 제자리에서 고치기도 하는데(순서 바꾸기·
  // 행 덧붙이기), 응답 객체를 그대로 넘기면 그 수정이 캐시·로깅이 들고 있는 응답까지 바꾼다
  const content = Array.isArray(result.content) ? result.content.slice() : []
  const number = typeof result.number === 'number' && result.number >= 0 ? Math.floor(result.number) : 0
  const total =
    typeof result.totalElements === 'number' && result.totalElements >= 0 ? result.totalElements : content.length

  return { currPage: number, tableData: content, totalElements: total }
}

/**
 * 총건수·페이지 크기로 **마지막 유효 페이지**(0-base)를 구한다.
 *
 * 서버는 요청한 `number` 를 그대로 돌려주고 범위를 넘어도 되돌리지 않는다(`JdbcPageQuery` 는
 * `PageImpl(rows, 요청 페이지, total)` 이다). 그래서 마지막 페이지의 마지막 행을 지우거나 조건이
 * 바뀌어 행이 줄면 화면이 「빈 표 + 켜진 페이지 번호」에 갇힌다 — 그 판정을 여기 한 곳이 소유한다.
 * 전량 모드(`pageSize` 0)와 0건은 언제나 0페이지다.
 *
 * @param {number} totalElements 서버가 말한 전체 건수
 * @param {number} pageSize 페이지 크기(0 = 전량)
 * @returns {number} 마지막 유효 페이지(0-base)
 */
export function lastPageOf(totalElements, pageSize) {
  const total = typeof totalElements === 'number' && totalElements > 0 ? totalElements : 0
  const size = typeof pageSize === 'number' && pageSize > 0 ? pageSize : 0

  if (total === 0 || size === 0) return 0

  return Math.ceil(total / size) - 1
}

/**
 * 봉투에서 **행 목록만** 꺼낸다 — 페이지 UI 가 없는 소비자용.
 *
 * 같은 엔드포인트를 목록 화면 말고 **셀렉트·검색창·POS 임베드**가 같이 부른다. 그쪽은 페이지 번호도
 * 전체 건수도 쓸 데가 없고 배열 하나만 있으면 된다. 그런 자리에서 `Array.isArray(res.data) ? … : []`
 * 를 각자 적어 두면 봉투가 온 날 **조용히 0건**이 된다 — 오류가 아니라 「메뉴가 없는 매장」으로 보인다.
 *
 * @param {*} result 서버 응답(페이지 봉투 또는 배열)
 * @returns {Array} 행 목록(언제나 배열)
 */
export function pageRows(result) {
  return applyPageResult(result).tableData
}

/**
 * **이 조건에서 가장 큰 값 한 행**만 묻는 조회 조건 — [nextSequence] 와 한 쌍이다.
 *
 * 「새 항목의 순번은 지금 있는 것 다음」 같은 값은 종전에 화면이 갖고 있던 목록에서 구했다.
 * 서버 페이징 뒤로 그 목록은 **한 장**이라, 2페이지에 더 큰 값이 있어도 화면은 모른다.
 * 전량을 다시 받아 최댓값을 세는 것은 그 한 값을 위해 수천 행을 옮기는 일이므로,
 * 같은 목록 API 에 **내림차순 첫 행 하나**를 묻는다 — 서버는 바뀌지 않는다.
 *
 * 화면의 검색 조건을 그대로 물려받는다: 최댓값은 사람이 보고 있는 그 목록의 최댓값이어야 한다.
 *
 * @param {object} searchForm 화면의 검색 조건(페이징 축 포함)
 * @param {string} sortProp 최댓값을 구할 열
 * @returns {object} 요청에 실을 새 객체
 */
export function topRowQuery(searchForm, sortProp) {
  return Object.assign({}, searchForm, {
    pageSize: 1,
    currPage: 0,
    sortProp: sortProp,
    sortOrder: SORT_ORDER.DESC,
  })
}

/**
 * [topRowQuery] 의 답을 **다음 순번**으로 — 행이 없으면 1부터다.
 *
 * 숫자가 아닌 값·음수는 「아직 없다」와 같이 본다. 이 값은 사람이 폼에서 고쳐 저장하는
 * 기본값이라 틀려도 되돌릴 수 있지만, 0 이나 `NaN` 이 칸에 앉으면 저장이 막힌다.
 *
 * @param {Array} rows 내림차순 첫 장(행 0개 또는 1개)
 * @param {string} prop 순번 열
 * @returns {number} 1 이상의 정수
 */
export function nextSequence(rows, prop) {
  const top = (rows || [])[0]
  const value = top && typeof top[prop] === 'number' && top[prop] > 0 ? Math.floor(top[prop]) : 0

  return value + 1
}

/**
 * Element UI 의 `sort-change` 이벤트를 서버 정렬 축으로 옮긴다.
 *
 * 세 번째 클릭(정렬 해제)은 `order: null` 로 오고, 열에 `prop` 이 없는 머리글(행 번호 칸)은 `prop` 이 없다 —
 * **어느 쪽이든 답은 「정렬 축 없음」**이고 그러면 서버 기본 정렬이다. 알 수 없는 `order` 값도 같다:
 * 화면이 모르는 낱말을 서버로 흘려보내지 않는다.
 *
 * **별칭**([aliases])은 열의 `prop` 과 서버의 `sortProp` 이 갈리는 자리를 잇는다. 열이 중첩 값을 그리면
 * (`cust.custNm`·`orderStatus.nmLan`) 그 점 표기는 **화면의 어휘**지 서버의 컬럼 이름이 아니다. 서버는
 * 화이트리스트 밖의 낱말을 무시하므로, 별칭이 없으면 그 열은 눌러도 아무 일이 없는 머리글이 된다 —
 * 오류가 아니라 **조용히 안 되는 것**이라 더 나쁘다. 회차 2 의 다섯 화면이 그런 열을 여덟 개 가지므로
 * 변환을 화면마다 적지 않고 여기 한 곳에 둔다.
 *
 * **이 표를 「서버가 점 표기도 받으니 없애도 된다」로 지우지 마라.** 한 번 지웠다가 되돌렸다
 * (2026-09-18): compose 의 사용자 목록에서 `sortProp=cust.custNm` 이 그대로 나가 정렬이 되지
 * 않았다. 화살표는 켜지므로 화면만 봐서는 알 수 없고, 서버가 두 표기를 다 받는지는 배포된
 * 판마다 다르다. 평면 이름은 어느 판에서나 화이트리스트에 있다.
 *
 * @param {{prop?: string, order?: string}} event
 * @param {Object<string, ?string>} [aliases] 열 `prop` → 서버 `sortProp`. 값이 비면 「정렬 축 없음」
 * @returns {{sortProp: ?string, sortOrder: ?string}}
 */
export function nextSort(event, aliases) {
  const prop = event && event.prop
  const order = event && event.order

  if (!prop || (order !== SORT_ORDER.ASC && order !== SORT_ORDER.DESC)) {
    return { sortProp: null, sortOrder: null }
  }

  const sortProp = aliases && Object.prototype.hasOwnProperty.call(aliases, prop) ? aliases[prop] : prop

  if (!sortProp) {
    return { sortProp: null, sortOrder: null }
  }

  return { sortProp: sortProp, sortOrder: order }
}

/**
 * 서버로 보낼 쿼리 — 정렬 축이 비면 **키 자체를 뺀다**.
 *
 * 둘 중 하나만 있는 것은 뜻이 없으므로(방향 없는 컬럼·컬럼 없는 방향) 같이 뺀다.
 *
 * @param {object} searchForm
 * @returns {object} 요청에 실을 새 객체
 */
export function pagingQuery(searchForm) {
  const query = Object.assign({}, searchForm)

  if (!query.sortProp || !query.sortOrder) {
    delete query.sortProp
    delete query.sortOrder
  }

  return query
}
